import type { PairSample, ResolvedProbe } from './classifier';
import type { CoverageState, LoadLevel, WindowSummary } from './types';

export const WINDOW_MS = 10_000;
export const SEAL_DELAY_MS = 3_000;

interface Bucket {
    coverage: Record<CoverageState, number>;
    backgroundMs: number;
    probes: ResolvedProbe[];
    ipdv: number[];
    up: number[];
    silenceMaxMs: number;
    loadSentBytes: number;
    loadReceivedBytes: number;
}

interface LevelChange {
    atMono: number;
    level: LoadLevel;
    kbps: number;
}

const emptyBucket = (): Bucket => ({
    coverage: { measured: 0, stalled: 0, blocked: 0, noSession: 0 },
    backgroundMs: 0,
    probes: [],
    ipdv: [],
    up: [],
    silenceMaxMs: 0,
    loadSentBytes: 0,
    loadReceivedBytes: 0
});

export function percentile(sorted: number[], q: number): number | null {
    if (sorted.length === 0) return null;
    return sorted[Math.min(sorted.length - 1, Math.max(0, Math.ceil(q * sorted.length) - 1))];
}

/**
 * The four coverage values [measured, stalled, blocked, noSession] as whole milliseconds that add up
 * to exactly `windowMs`, as the Dashboard demands (spec 4.4). Time no state accounted for is
 * noSession; time counted twice comes off measured first. Then largest-remainder rounding: rounding
 * each value on its own can miss the total by one or two (3000.5 + 6999.5 rounds to 10 001).
 */
export function coverageMs(raw: [number, number, number, number], windowMs: number): [number, number, number, number] {
    const v = raw.map((x) => Math.max(0, x));
    const accounted = v[0] + v[1] + v[2] + v[3];
    if (accounted < windowMs) v[3] += windowMs - accounted;
    let excess = accounted - windowMs;
    for (const i of [0, 3, 2, 1]) {
        if (excess <= 0) break;
        const cut = Math.min(v[i], excess);
        v[i] -= cut;
        excess -= cut;
    }
    const out = v.map(Math.floor);
    const byRemainder = [0, 1, 2, 3].sort((a, b) => v[b] - out[b] - (v[a] - out[a]) || a - b);
    let missing = windowMs - (out[0] + out[1] + out[2] + out[3]);
    for (let k = 0; missing > 0 && k < 4; k++, missing--) out[byRemainder[k]]++;
    return out as [number, number, number, number];
}

const round1 = (x: number | null) => (x === null ? null : Math.round(x * 10) / 10);
const sorted = (xs: number[]) => [...xs].sort((a, b) => a - b);

/**
 * Collects one target's measurements into 10-second windows cut on send time (spec 4.4) and seals
 * each window once every probe in it has passed its deadline.
 */
export class WindowAggregator {
    private readonly buckets = new Map<number, Bucket>();
    private nextToSeal = 0;
    private lastArrival?: number;
    private levels: LevelChange[];

    constructor(
        private readonly target: string,
        private readonly startMono: number,
        private readonly startWall: number,
        private readonly windowMs = WINDOW_MS,
        private readonly sealDelayMs = SEAL_DELAY_MS
    ) {
        this.levels = [{ atMono: startMono, level: 'off', kbps: 0 }];
    }

    addCoverage(state: CoverageState, fromMono: number, toMono: number): void {
        this.spread(fromMono, toMono, (b, ms) => (b.coverage[state] += ms));
    }

    /** Time in which the page was hidden; it overlaps the coverage states (spec 4.4). */
    addBackground(fromMono: number, toMono: number): void {
        this.spread(fromMono, toMono, (b, ms) => (b.backgroundMs += ms));
    }

    /**
     * A `stalled` probe is not judged (spec 4.3): it stays out of the counts, and the interval it
     * stood for, `intervalMs`, moves from measured to stalled.
     */
    addProbes(probes: ResolvedProbe[], intervalMs: number): void {
        for (const p of probes) {
            const b = this.bucket(this.index(p.sendMono));
            if (!b) continue;
            if (p.outcome.kind !== 'stalled') b.probes.push(p);
            else {
                b.coverage.measured -= intervalMs;
                b.coverage.stalled += intervalMs;
            }
        }
    }

    addPairs(pairs: PairSample[]): void {
        for (const p of pairs) {
            const b = this.bucket(this.index(p.atMono));
            if (!b) continue;
            b.ipdv.push(p.ipdvMs);
            b.up.push(p.upMs);
        }
    }

    echoArrived(atMono: number): void {
        this.silence(atMono);
        this.lastArrival = atMono;
    }

    sessionEnded(atMono: number): void {
        this.silence(atMono);
        this.lastArrival = undefined;
    }

    /** Restarts the silence clock: time in which the page could not send or receive (a frozen tab) is not silence. */
    restartSilence(atMono: number): void {
        if (this.lastArrival !== undefined) this.lastArrival = atMono;
    }

    setLoadLevel(level: LoadLevel, kbps: number, atMono: number): void {
        this.levels.push({ atMono, level, kbps });
    }

    addLoadSent(bytes: number, atMono: number): void {
        const b = this.bucket(this.index(atMono));
        if (b) b.loadSentBytes += bytes;
    }

    addLoadReceived(bytes: number, atMono: number): void {
        const b = this.bucket(this.index(atMono));
        if (b) b.loadReceivedBytes += bytes;
    }

    /** Windows whose end plus the seal delay has passed. */
    seal(nowMono: number): WindowSummary[] {
        return this.sealWhile((end) => nowMono >= end + this.sealDelayMs);
    }

    /** Every window that has started, without waiting: used when the measurement stops. */
    flush(nowMono: number): WindowSummary[] {
        return this.sealWhile((end) => end - this.windowMs < nowMono);
    }

    private sealWhile(due: (end: number) => boolean): WindowSummary[] {
        const out: WindowSummary[] = [];
        for (;;) {
            const n = this.nextToSeal;
            const start = this.startMono + n * this.windowMs;
            const end = start + this.windowMs;
            if (!due(end)) break;
            out.push(this.summarise(n, start, end, this.buckets.get(n) ?? emptyBucket()));
            this.buckets.delete(n);
            this.nextToSeal++;
        }
        return out;
    }

    /** Adds the span [fromMono, toMono) to the windows it covers, split at the window boundaries. */
    private spread(fromMono: number, toMono: number, add: (b: Bucket, ms: number) => void): void {
        let t = fromMono;
        while (t < toMono) {
            const n = this.index(t);
            const end = Math.min(toMono, this.startMono + (n + 1) * this.windowMs);
            const b = this.bucket(n);
            if (b) add(b, end - t);
            t = end;
        }
    }

    private index(mono: number): number {
        return Math.floor((mono - this.startMono) / this.windowMs);
    }

    /** The bucket for window n, or undefined when n is before the start or already sealed. */
    private bucket(n: number): Bucket | undefined {
        if (n < this.nextToSeal || n < 0) return undefined;
        let b = this.buckets.get(n);
        if (!b) {
            b = emptyBucket();
            this.buckets.set(n, b);
        }
        return b;
    }

    private silence(atMono: number): void {
        if (this.lastArrival === undefined) return;
        const b = this.bucket(this.index(atMono));
        if (b) b.silenceMaxMs = Math.max(b.silenceMaxMs, atMono - this.lastArrival);
    }

    private summarise(n: number, start: number, end: number, b: Bucket): WindowSummary {
        const tcp = b.probes.some((p) => p.transport === 'tcp');
        const count = (kind: string) => b.probes.filter((p) => p.outcome.kind === kind).length;
        const echoed = count('echoed');
        const rtts = sorted(b.probes.flatMap((p) => ('rttMs' in p.outcome && p.outcome.rttMs !== undefined ? [p.outcome.rttMs] : [])));
        const ipdv = sorted(b.ipdv);
        const up = sorted(b.up);

        const c = b.coverage;
        const [measured, stalled, blocked, noSession] = coverageMs([c.measured, c.stalled, c.blocked, c.noSession], this.windowMs);

        const load = this.levelFor(start, end);
        return {
            target: this.target,
            window: n,
            windowStart: this.startWall + n * this.windowMs,
            transport: b.probes.length === 0 ? 'none' : tcp ? 'tcp' : 'udp',
            sent: b.probes.length,
            echoed,
            lostUp: tcp ? null : count('up'),
            lostDown: tcp ? null : count('down'),
            lostUnknown: tcp ? null : count('unknown'),
            late: tcp ? b.probes.length - echoed : null,
            rttP50: round1(percentile(rtts, 0.5)),
            rttP95: round1(percentile(rtts, 0.95)),
            rttMax: round1(rtts.length ? rtts[rtts.length - 1] : null),
            jitterP50: round1(percentile(ipdv, 0.5)),
            jitterP95: round1(percentile(ipdv, 0.95)),
            jitterUpP50: round1(percentile(up, 0.5)),
            jitterUpP95: round1(percentile(up, 0.95)),
            silenceMaxMs: Math.round(b.silenceMaxMs),
            measuredMs: measured,
            stalledMs: stalled,
            blockedMs: blocked,
            noSessionMs: noSession,
            backgroundMs: Math.min(this.windowMs, Math.round(b.backgroundMs)),
            loadLevel: load.level,
            loadTargetKbps: load.kbps,
            // bytes * 8 / ms = kbit/s
            loadSentKbps: Math.round((b.loadSentBytes * 8) / this.windowMs),
            loadReceivedKbps: Math.round((b.loadReceivedBytes * 8) / this.windowMs)
        };
    }

    private levelFor(start: number, end: number): { level: LoadLevel | 'mixed'; kbps: number } {
        const spans = this.levels
            .map((c, i) => ({ c, from: Math.max(start, c.atMono), to: Math.min(end, this.levels[i + 1]?.atMono ?? Infinity) }))
            .filter((s) => s.to > s.from);
        if (spans.length === 0) return { level: 'off', kbps: 0 };
        if (new Set(spans.map((s) => s.c.level)).size === 1 && new Set(spans.map((s) => s.c.kbps)).size === 1) {
            return { level: spans[0].c.level, kbps: spans[0].c.kbps };
        }
        const weighted = spans.reduce((sum, s) => sum + s.c.kbps * (s.to - s.from), 0);
        return { level: 'mixed', kbps: Math.round(weighted / (end - start)) };
    }
}
